import { useCallback } from 'react';
import { getTaskWithSubtasks, getAllSubtaskIds, buildTaskTree, flattenTaskTree } from '../utils/taskUtils';
import { renumberSequentialListAroundTask } from '../utils/sequentialTasks';
import {
  determineDeletionMode,
  calculateSiblingOrderShifts,
  calculateSubtaskReparentingOrders,
  findTargetTaskAfterDeletion
} from '../utils/taskDeletion';

/**
 * Hook for handling task deletion operations
 * @param {Object} params - Configuration object
 * @param {Array} params.tasks - All tasks
 * @param {Set} params.expandedTasks - Set of expanded task IDs
 * @param {string|null} params.editingTaskId - Currently editing task ID
 * @param {Function} params.setEditingTaskId - Function to set editing task ID
 * @param {Object} params.editingElementRef - Ref to the editing element
 * @param {Function} params.updateTaskOrder - Function to update a single task order
 * @param {Function} params.updateTaskOrders - Function to batch update task orders
 * @param {Function} params.updateTaskParentWithTextAndOrder - Function to update task parent and order
 * @param {Function} params.deleteTaskAPI - Function to delete a task via API
 * @param {boolean} params.isLoggedIn - Whether user is logged in
 * @param {Object} params.pendingFocusRef - Ref for pending focus (shared with keyboard handler)
 */
export function useTaskDeletion({
  tasks,
  expandedTasks,
  editingTaskId,
  setEditingTaskId,
  editingElementRef,
  updateTaskOrder,
  updateTaskOrders,
  updateTaskParentWithTextAndOrder,
  deleteTaskAPI,
  isLoggedIn,
  pendingFocusRef
}) {
  const handleDeleteTask = useCallback(async (taskId, deleteMode = null, origin) => {
    const taskWithSubtasks = getTaskWithSubtasks(taskId, tasks);
    const hasSubtasks = taskWithSubtasks && taskWithSubtasks.subtasks && taskWithSubtasks.subtasks.length > 0;
    
    // Determine deletion mode
    deleteMode = determineDeletionMode(taskId, tasks, deleteMode);

    try {
      // Handle task-only deletion with subtask reparenting
      if (deleteMode === 'task-only' && hasSubtasks) {
        const currentTask = tasks.find(t => t.id === taskId);
        if (!currentTask) return;
        
        const directSubtasks = taskWithSubtasks.subtasks || [];
        
        // Calculate which siblings need order shifts
        const siblingsToShift = calculateSiblingOrderShifts(currentTask, directSubtasks, tasks);
        
        // Update sibling orders
        for (const sibling of siblingsToShift) {
          if (sibling.newOrder !== null && sibling.newOrder !== undefined) {
            await updateTaskOrder(sibling.id, sibling.newOrder);
          }
        }
        
        // Calculate reparenting orders for subtasks
        const reparentingOrders = calculateSubtaskReparentingOrders(currentTask, directSubtasks);
        
        // Reparent subtasks
        for (const { subtask, newOrder } of reparentingOrders) {
          const actualSubtask = tasks.find(t => t.id === subtask.id);
          if (!actualSubtask) continue;
          
          const subtaskText = actualSubtask.text || '';
          const newParentId = currentTask.parent_task_id;
          await updateTaskParentWithTextAndOrder(actualSubtask.id, newParentId, subtaskText, newOrder);
        }
      } 
      // Handle task-and-subtasks deletion
      else if (deleteMode === 'task-and-subtasks' && hasSubtasks) {
        // Only make API requests if logged in
        if (!isLoggedIn) {
          console.warn('Cannot delete tasks when logged out');
          return;
        }
        const subtaskIds = getAllSubtaskIds(taskWithSubtasks);
        for (const subtaskId of subtaskIds) {
          try {
            const response = await fetch('/api/tasks.php', {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ id: subtaskId })
            });
            if (!response.ok) {
              console.error('Error deleting subtask:', subtaskId);
            }
          } catch (error) {
            console.error('Error deleting subtask:', error);
          }
        }
      }

      // Find target task to focus after deletion
      const targetTask = findTargetTaskAfterDeletion(taskId, tasks, expandedTasks);

      // Delete the task
      const success = deleteTaskAPI(taskId, origin);

      if (success) {
        // Clear editing state if deleting the currently edited task
        const deletedTask = tasks.find(t => t.id === taskId);
        if (deletedTask && editingTaskId === deletedTask._reactKey) {
          setEditingTaskId(null);
          editingElementRef.current = null;
        }

        // Renumber sequential list around the focused task (e.g. 1. a 2. b 3. c → delete 2. b → 1. a 2. c)
        const tasksAfterDelete = tasks.filter(t => t.id !== taskId);
        if (targetTask && updateTaskOrders) {
          const renumberUpdates = renumberSequentialListAroundTask(targetTask, tasksAfterDelete);
          if (renumberUpdates.length > 0) {
            try {
              const batch = renumberUpdates.map(update => {
                const task = tasksAfterDelete.find(t => t.id === update.id);
                return { id: update.id, order: task ? task.order : undefined, text: update.text };
              });
              await updateTaskOrders(batch, true);
            } catch (err) {
              console.error('Error renumbering sequential tasks after delete:', err);
            }
          }
        }

        // Focus target task after deletion via pendingFocusRef (consumed by TaskTable effect)
        if (targetTask && editingTaskId !== targetTask._reactKey && pendingFocusRef) {
          const runFocus = (targetTaskElement) => {
            const selection = window.getSelection();
            let caretAlreadyPositioned = false;
            if (selection.rangeCount > 0) {
              const existingRange = selection.getRangeAt(0);
              if (targetTaskElement.contains(existingRange.startContainer)) {
                const preCaretRange = existingRange.cloneRange();
                preCaretRange.selectNodeContents(targetTaskElement);
                preCaretRange.setEnd(existingRange.endContainer, existingRange.endOffset);
                const currentPos = preCaretRange.toString().length;
                const textLength = targetTaskElement.textContent.length;
                if (currentPos < textLength) {
                  caretAlreadyPositioned = true;
                }
              }
            }
            editingElementRef.current = targetTaskElement;
            targetTaskElement.focus();
            if (!caretAlreadyPositioned) {
              const range = document.createRange();
              range.selectNodeContents(targetTaskElement);
              range.collapse(false);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          };
          pendingFocusRef.current = { reactKey: targetTask._reactKey, runFocus };
          setEditingTaskId(targetTask._reactKey);
        }
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  }, [
    tasks,
    expandedTasks,
    editingTaskId,
    setEditingTaskId,
    editingElementRef,
    updateTaskOrder,
    updateTaskOrders,
    updateTaskParentWithTextAndOrder,
    deleteTaskAPI,
    isLoggedIn,
    pendingFocusRef
  ]);

  return { handleDeleteTask };
}
